import { z } from 'zod'

// Org-level invoice defaults, stored as system-preference JSON under
// INVOICE_SETTINGS_KEYS. Field names match the values organizations have
// already saved from the invoice Settings tab — numeric-ish fields stay
// strings for backward compatibility with that stored data.

export const INVOICE_SETTINGS_KEYS = {
  SETTINGS: 'invoice.settings',
  PAYMENTS: 'invoice.payments',
} as const

export const InvoiceModuleSettingsSchema = z.object({
  defaultCurrency: z.string().default('USD'),
  defaultPaymentTerms: z.string().default('Net 30'),
  invoicePrefix: z.string().default('INV-'),
  nextInvoiceNumber: z.string().default('1001'),
  defaultTaxRate: z.string().default('0'),
  emailEnabled: z.boolean().default(true),
  emailFromName: z.string().default(''),
  emailReplyTo: z.string().default(''),
  emailSubjectTemplate: z
    .string()
    .default('Invoice #{invoice_number} from {company_name}'),
  emailMessageTemplate: z
    .string()
    .default(
      'Hi,\n\nPlease find attached invoice {invoice_number} for {total_amount}, due {due_date}.\n\nThank you,\n{company_name}',
    ),
  acceptOnlinePayments: z.boolean().default(false),
  paymentMethods: z.array(z.string()).default([]),
  latePaymentReminders: z.boolean().default(true),
  reminderDays: z.string().default('7,14,30'),
  showCompanyLogo: z.boolean().default(true),
  showPaymentInstructions: z.boolean().default(true),
  defaultNotes: z.string().default(''),
  defaultTermsAndConditions: z.string().default(''),
})

export type InvoiceModuleSettings = z.infer<typeof InvoiceModuleSettingsSchema>

export const InvoicePaymentSettingsSchema = z.object({
  acceptBankTransfer: z.boolean().default(true),
  acceptCreditCard: z.boolean().default(false),
  acceptPayPal: z.boolean().default(false),
  acceptCheck: z.boolean().default(true),
  acceptCash: z.boolean().default(false),
  bankName: z.string().default(''),
  bankAccountName: z.string().default(''),
  bankAccountNumber: z.string().default(''),
  bankRoutingNumber: z.string().default(''),
  bankSwiftCode: z.string().default(''),
  paypalEmail: z.string().default(''),
  paymentInstructions: z.string().default(''),
})

export type InvoicePaymentSettings = z.infer<
  typeof InvoicePaymentSettingsSchema
>

export const getDefaultInvoiceModuleSettings = (): InvoiceModuleSettings =>
  InvoiceModuleSettingsSchema.parse({})

export const getDefaultInvoicePaymentSettings = (): InvoicePaymentSettings =>
  InvoicePaymentSettingsSchema.parse({})

/**
 * Parse a stored settings blob tolerantly: unknown/legacy fields are
 * dropped, missing fields fall back to defaults, and a malformed blob
 * degrades to all-defaults instead of throwing.
 */
export function parseInvoiceModuleSettings(
  value: unknown,
): InvoiceModuleSettings {
  const result = InvoiceModuleSettingsSchema.safeParse(value ?? {})
  return result.success ? result.data : getDefaultInvoiceModuleSettings()
}

export function parseInvoicePaymentSettings(
  value: unknown,
): InvoicePaymentSettings {
  const result = InvoicePaymentSettingsSchema.safeParse(value ?? {})
  return result.success ? result.data : getDefaultInvoicePaymentSettings()
}

// Variables available in invoice email subject/message templates.
export interface InvoiceTemplateVariables {
  invoice_number: string
  company_name: string
  total_amount: string
  due_date: string
  entity_name: string
}

export const INVOICE_TEMPLATE_VARIABLES: Array<{
  token: keyof InvoiceTemplateVariables
  description: string
}> = [
  { token: 'invoice_number', description: 'The invoice number, e.g. INV-1001' },
  { token: 'company_name', description: 'Your organization name' },
  { token: 'total_amount', description: 'Invoice total with currency' },
  { token: 'due_date', description: 'The invoice due date' },
  { token: 'entity_name', description: 'The client being billed' },
]

/**
 * Substitute {token} placeholders in an invoice email template. Unknown
 * tokens are left as-is so typos are visible rather than silently blank.
 */
export function renderInvoiceTemplate(
  template: string,
  variables: Partial<InvoiceTemplateVariables>,
): string {
  return template.replace(/\{([a-z_]+)\}/g, (match, token: string) => {
    const value = variables[token as keyof InvoiceTemplateVariables]
    return value !== undefined && value !== null ? value : match
  })
}
